import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect } from 'react';
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CreditCard, Lock, ArrowLeft, Check, Shield, Plus, X, Loader2 } from 'lucide-react';
import { useCustomAuth } from '@/contexts/CustomAuthContext';
import { cardService } from '@/services/cardService';
import { subscriptionService } from '@/services/subscriptionService';
import { cupomService, type StripeCouponData } from '@/services/cupomService';
import useUserPlan from '@/hooks/useUserPlan';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { CardBrandIcon } from '@/components/finance/CardBrandIcon';

interface SavedCard { id_Stripe_Card: string; default_Card?: boolean; last_Digits?: string; brand?: string }

interface PlanDetails {
  name: string;
  price: number;
  plays: string;
  features: string[];
  isPopular: boolean;
}

const planDetails: Record<string, PlanDetails> = {
  light: {
    name: 'Light',
    price: 97,
    plays: '6.000',
    get features() { return [translateInterfaceText("Vídeos ilimitados"), '6.000 Plays']; },
    isPopular: false,
  },
  plus: {
    name: 'Plus',
    price: 287,
    plays: '30.000',
    get features() { return [translateInterfaceText("Vídeos ilimitados"), '30.000 Plays']; },
    isPopular: false,
  },
  premium: {
    name: 'Premium',
    price: 587,
    plays: '60.000',
    get features() { return [translateInterfaceText("Vídeos ilimitados"), '60.000 Plays']; },
    isPopular: false,
  },
};

interface CheckoutFormProps {
  selectedPlan: string;
  setSelectedPlan: (plan: string) => void;
  // Cupom promocional vindo do link de divulgação. Quando presente, o input
  // do cupom fica travado (não editável) e o preço reflete o desconto.
  lockedCoupon?: string;
}

// Promoções com cupom travado: id do cupom -> desconto (%) no 1º mês.
// O valor real cobrado vem do Stripe (o BE aplica discounts:[{coupon}]);
// este mapa serve só pra exibir o cálculo na UI e deve bater com o Stripe.
const PROMO_FIRST_MONTH_DISCOUNTS: Record<string, number> = {
  IxtM7I8i: 50,
};

export default function CheckoutForm({ selectedPlan, setSelectedPlan, lockedCoupon }: CheckoutFormProps) {
  useLanguage();
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { user, refreshUser } = useCustomAuth();
  const queryClient = useQueryClient();
  const [pendingCheckout, setPendingCheckout] = useState<ReturnType<typeof subscriptionService.getPendingCheckout>>(null);
  const [pendingError, setPendingError] = useState('');
  const [checkingPending, setCheckingPending] = useState(true);
  useEffect(() => {
    if (!user?.id_Stripe_Client) {
      setCheckingPending(false);
      setPendingError('Cliente de cobrança não encontrado. Atualize a página ou entre em contato com o suporte.');
      return;
    }
    let active = true;
    setCheckingPending(true);
    setPendingError('');
    setPendingCheckout(null);
    void subscriptionService.recoverPendingCheckout(user.id_Stripe_Client).then(result => {
      if (!active) return;
      setCheckingPending(false);
      if (result.success !== true || !result.data) {
        setPendingError(result.message || 'Não foi possível consultar os pagamentos pendentes. Atualize a página antes de pagar.');
      } else if (result.data.kind === 'surplus') {
        setPendingError('Há uma compra de plays extras aguardando confirmação. Consulte essa compra no painel antes de contratar um plano.');
      } else setPendingCheckout(result.data.pending);
    });
    return () => { active = false; };
  }, [user?.id_Stripe_Client]);

  const [isLoading, setIsLoading] = useState(false);
  const [cardName, setCardName] = useState('');
  const [couponCode, setCouponCode] = useState(lockedCoupon || '');
  const isCouponLocked = !!lockedCoupon;
  const [couponStatus, setCouponStatus] = useState<'idle' | 'validating' | 'valid' | 'invalid'>('idle');
  const [validatedCoupon, setValidatedCoupon] = useState<StripeCouponData | null>(null);
  const [savedCards, setSavedCards] = useState<SavedCard[]>([]);
  const [useNewCard, setUseNewCard] = useState(true);
  const [selectedCardId, setSelectedCardId] = useState<string>('');

  // Get existing user plan data
  const { plan, idStripeProduct, isLoading: loadingPlan, isTrial, activeSubscription, hasSubscriptionHistory, isError: planError, isFetching: fetchingPlan } = useUserPlan();
  const eligibleForTrial = !hasSubscriptionHistory && !planError;

  // Environment variables
  const lightPlanVslDynamic97 = import.meta.env.VITE_PROD_LIGHT_VSL_DYNAMIC97;
  const plusPlan = import.meta.env.VITE_PROD_PLUS;
  const premiumPlan = import.meta.env.VITE_PROD_PREMIUM;

  const planProductMap: Record<string, string> = { light: lightPlanVslDynamic97, plus: plusPlan, premium: premiumPlan };
  const selectedProduct = planProductMap[selectedPlan];
  const currentPlan = planDetails[selectedPlan] || planDetails.plus;
  const formatBRL = (value: number) => value.toFixed(2).replace('.', ',');

  // --- Desconto efetivo do cupom ---
  // Cupom promocional travado usa o mapa fixo; cupom digitado usa o desconto
  // real retornado pelo Stripe na validação (percent_off / amount_off).
  const lockedPercentOff = isCouponLocked ? PROMO_FIRST_MONTH_DISCOUNTS[couponCode] ?? 0 : 0;
  const validatedPercentOff =
    couponStatus === 'valid' && validatedCoupon?.percent_off ? Number(validatedCoupon.percent_off) : 0;
  const validatedAmountOff =
    couponStatus === 'valid' && validatedCoupon?.amount_off ? Number(validatedCoupon.amount_off) / 100 : 0;

  const firstMonthDiscountPct = lockedPercentOff || validatedPercentOff;
  const firstMonthPrice =
    firstMonthDiscountPct > 0
      ? currentPlan.price * (1 - firstMonthDiscountPct / 100)
      : Math.max(0, currentPlan.price - validatedAmountOff);
  const hasFirstMonthDiscount = firstMonthPrice < currentPlan.price;

  // Monta os textos do desconto conforme a duração (Stripe: once/repeating/forever).
  const discountText =
    firstMonthDiscountPct > 0
      ? `${firstMonthDiscountPct}% OFF`
      : validatedAmountOff > 0
      ? `R$ ${formatBRL(validatedAmountOff)} OFF`
      : '';
  const discountDuration = isCouponLocked ? 'once' : validatedCoupon?.duration;
  let discountBadge = '';
  let discountSummaryLeft = '';
  let showAfterPrice = false;
  if (hasFirstMonthDiscount) {
    if (discountDuration === 'forever') {
      discountBadge = `${discountText} por mês`;
      discountSummaryLeft = `Todo mês (${discountText})`;
    } else if (discountDuration === 'repeating' && validatedCoupon?.duration_in_months) {
      const n = validatedCoupon.duration_in_months;
      discountBadge = `${discountText} nos primeiros ${n} meses`;
      discountSummaryLeft = `Primeiros ${n} meses (${discountText})`;
      showAfterPrice = true;
    } else {
      discountBadge = `${discountText} no 1º mês`;
      discountSummaryLeft = `1º mês (${discountText})`;
      showAfterPrice = true;
    }
  }

  // Valida o cupom digitado ao perder o foco (não há botão de "ativar").
  const handleCouponBlur = async () => {
    if (isCouponLocked) return;
    const code = couponCode.trim();
    if (!code) {
      setCouponStatus('idle');
      setValidatedCoupon(null);
      return;
    }
    setCouponStatus('validating');
    const res = await cupomService.validate(code);
    // valid === false = cupom existe mas está expirado/esgotado no Stripe.
    if (res?.success && res?.data && res.data.valid !== false) {
      setValidatedCoupon(res.data);
      setCouponStatus('valid');
    } else {
      setValidatedCoupon(null);
      setCouponStatus('invalid');
    }
  };

  // Buscar cartões salvos quando componente montar
  useEffect(() => {
    const fetchSavedCards = async () => {
      if (user?.id_Stripe_Client) {
        try {
          const response = await cardService.getAllUserCard(user.id_Stripe_Client);

          // A resposta é { success: true, data: [...] }
          if (response.success && response.data) {
            const cards = Array.isArray(response.data) ? response.data : [];

            if (cards.length > 0) {
              setSavedCards(cards);
              // Se tem cartões salvos, seleciona o padrão ou o primeiro
              const defaultCard = cards.find((card: SavedCard) => card.default_Card === true);
              if (defaultCard) {
                setSelectedCardId(defaultCard.id_Stripe_Card);
                setUseNewCard(false);
              } else {
                setSelectedCardId(cards[0].id_Stripe_Card);
                setUseNewCard(false);
              }
            }
          }
        } catch (error) {
          console.error('Erro ao buscar cartões:', error);
        }
      }
    };

    fetchSavedCards();
  }, [user?.id_Stripe_Client]);

  // Mantém o cupom em sincronia com o link promocional (cupom travado).
  useEffect(() => {
    if (lockedCoupon) setCouponCode(lockedCoupon);
  }, [lockedCoupon]);

  // Determinar se o plano está ativo e qual é
  const getCurrentPlanKey = () => {
    if (!plan) return null;
    const planNameLower = plan.toLowerCase();
    if (planNameLower.includes('light')) return 'light';
    if (planNameLower.includes('plus')) return 'plus';
    if (planNameLower.includes('premium')) return 'premium';
    return null;
  };

  const accountActive = !!activeSubscription;
  const currentUserPlan = accountActive ? getCurrentPlanKey() : null;
  const hasActivePlan = !!currentUserPlan && !isTrial;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isLoading || checkingPending || pendingError || (!pendingCheckout && (planError || fetchingPlan))) return;
    if (!pendingCheckout && (!stripe || !elements)) {
      return;
    }

    // Não deixa enviar um cupom sabidamente inválido: o BE faria a assinatura
    // inteira falhar (Exception 404 'Cupom inválido').
    if (!pendingCheckout && couponStatus === 'invalid') {
      toast.error(translateInterfaceText("Cupom inválido. Corrija ou remova o cupom para continuar."));
      return;
    }

    setIsLoading(true);

    try {
      const idStripe = user?.id_Stripe_Client;

      if (!idStripe) {
        toast.error(translateInterfaceText("Usuário não encontrado"));
        setIsLoading(false);
        return;
      }

      if (!pendingCheckout && (!selectedProduct || (!isTrial && activeSubscription?.status === 'active' && idStripeProduct === selectedProduct))) {
        toast.error(translateInterfaceText("Você já possui este plano ativo. Escolha outro plano para alterar sua assinatura."));
        setIsLoading(false);
        return;
      }
      let cardIdForSubscription: string | undefined = useNewCard ? undefined : selectedCardId || undefined;

      if (!pendingCheckout && useNewCard) {
        const cardNumberElement = elements!.getElement(CardNumberElement);

        if (!cardNumberElement) {
          toast.error(translateInterfaceText("Erro ao processar cartão"));
          setIsLoading(false);
          return;
        }

        const res = await stripe!.createToken(cardNumberElement, {
          name: cardName,
        });

        if (res.error) {
          toast.error('Erro ao processar cartão: ' + res.error.message);
          setIsLoading(false);
          return;
        }

        const body = { token: res.token.id };
        const resCard = await cardService.createCard(idStripe, body);

        if (resCard.success !== true || !resCard?.data?.id_Stripe_Card) {
          setIsLoading(false);

          const errorMessages: Record<string, string> = {
            'Your card was declined.': translateInterfaceText("Seu cartão foi recusado, tente novamente."),
            'Your card has insufficient funds.': translateInterfaceText("Seu cartão não possui saldo suficiente."),
            'Your card has expired.': translateInterfaceText("Seu cartão está vencido."),
            "Your card's security code is incorrect.": translateInterfaceText("O código de segurança está incorreto."),
          };

          const message = errorMessages[resCard.message] || resCard.message || 'Não foi possível confirmar o cadastro do cartão. Tente novamente antes de pagar.';
          toast.error(message);
          return;
        }

        cardIdForSubscription = resCard?.data?.id_Stripe_Card || cardIdForSubscription;
      }

      if (pendingCheckout) toast.info(translateInterfaceText("Consultando a tentativa anterior com o plano, cupom e cartão já enviados. A seleção atual será usada somente em uma nova compra."));
      const result = await subscriptionService.checkout(idStripe, pendingCheckout || {
        product_id: selectedProduct,
        currency: 'brl',
        ...(couponCode.trim() ? { cupom: couponCode.trim() } : {}),
        ...(cardIdForSubscription ? { id_Stripe_Card: cardIdForSubscription } : {}),
        ...(isTrial ? { action: 'activate_trial' as const } : {}),
      });
      setPendingCheckout(subscriptionService.getPendingCheckout(idStripe));
      if (result.resumedDifferentSelection) toast.info(translateInterfaceText("A tentativa anterior foi consultada com o plano, cupom e cartão originais."));
      if (result.success !== true) {
        toast.error(result.message || translateInterfaceText("Não foi possível confirmar a assinatura. Consulte a mesma tentativa novamente."));
        setIsLoading(false);
        return;
      }
      sessionStorage.removeItem('signup_stripe_client_id');
      sessionStorage.removeItem('promo_coupon');
      sessionStorage.removeItem('promo_plan');
      if (result.data?.status === 'trialing' && !isCouponLocked) {
        sessionStorage.setItem('hs_show_oferta_after_signup', '1');
        sessionStorage.setItem('hs_purchased_plan_id', selectedPlan);
      }
      await Promise.all([refreshUser(), queryClient.invalidateQueries({ queryKey: ['userPlan', user?.id_user] })]);
      toast.success(translateInterfaceText("Assinatura confirmada com sucesso."));
      navigate('/dashboard');
      setIsLoading(false);
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error instanceof Error ? error.message : translateInterfaceText("Erro inesperado. Consulte a mesma tentativa novamente."));
      setIsLoading(false);
    }
  };

  if (loadingPlan) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="h-screen overflow-y-auto flex flex-col bg-gray-50">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white/80 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 py-4 relative flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>{translateInterfaceText("Voltar")}</span>
          </button>

          <img
            src="/assets/imgs/logo-azul-e-preta.png"
            alt={translateInterfaceText("HostVSL")}
            className="h-8 absolute left-1/2 -translate-x-1/2"
          />

          <div className="hidden sm:flex items-center gap-2 text-blue-600">
            <Shield className="h-3.5 w-3.5" />
            <span className="text-xs font-medium whitespace-nowrap">
              {translateInterfaceText("Pagamento 100% seguro")}</span>
          </div>
        </div>
      </div>

      {(pendingCheckout || pendingError || planError) && <p role="alert" className="mx-auto mt-4 max-w-3xl rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        {pendingError || (pendingCheckout ? translateInterfaceText("Há um pagamento aguardando confirmação. Consulte a tentativa anterior com o mesmo plano, cupom e cartão antes de iniciar outra compra.") : translateInterfaceText("Não foi possível confirmar seu histórico de assinaturas. Atualize a página antes de contratar."))}
      </p>}
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6 flex-1">
        {/* Plans Section Header */}
        <div className="mb-6">
          <h1 className="text-xl font-bold text-gray-900 mb-1">{translateInterfaceText("Escolha seu plano")}</h1>
          <p className="text-sm text-gray-600">
            {hasActivePlan ? translateInterfaceText("Altere seu plano atual") : eligibleForTrial ? translateInterfaceText("Primeira assinatura: 15 dias grátis") : isTrial ? translateInterfaceText("Ative seu plano com cobrança imediata") : translateInterfaceText("Contrate seu plano com cobrança imediata")}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 lg:items-stretch">
          {/* Left Column - Plans Selection */}
          <div className="h-full">
            <div className="h-full flex flex-col justify-between space-y-3">
              {Object.entries(planDetails).map(([planKey, planInfo]) => {
                const isCurrentPlan = currentUserPlan === planKey;
                const isDisabled = activeSubscription?.status === 'active' && idStripeProduct === planProductMap[planKey];

                return (
                  <div
                    key={planKey}
                    className={`bg-white rounded-lg p-3 transition-all duration-200 shadow-sm flex-1 ${
                      isDisabled
                        ? 'opacity-60 cursor-not-allowed border border-gray-300'
                        : selectedPlan === planKey
                        ? 'border-2 border-blue-600 bg-blue-50 cursor-pointer hover:shadow-md'
                        : 'border border-gray-200 hover:border-blue-300 cursor-pointer hover:shadow-md'
                    }`}
                    onClick={() => !isDisabled && setSelectedPlan(planKey)}
                  >
                  <div className="h-full flex flex-col">
                    <div className="space-y-1">
                      {/* Plan info */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-bold text-gray-900">{planInfo.name}</h3>
                          {isCurrentPlan && (
                            <span className="px-3 py-1 text-xs font-bold rounded-full bg-blue-100 text-blue-800 border border-blue-200">
                              {translateInterfaceText("Plano Atual")}</span>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="text-xl font-bold text-gray-900">R$ {planInfo.price}</div>
                          <div className="text-xs font-medium text-blue-600">
                            {eligibleForTrial ? translateInterfaceText("15 dias grátis") : translateInterfaceText("/mês")}
                          </div>
                        </div>
                      </div>

                      {/* Features */}
                      <div className="space-y-1.5 mt-2">
                        {planInfo.features.map((feature, index) => (
                          <div key={index} className="flex items-center gap-2 text-sm text-gray-700">
                            <Check className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />
                            {feature}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Select Button */}
                    <button
                      onClick={() => !isDisabled && setSelectedPlan(planKey)}
                      disabled={isDisabled}
                      className={`w-full py-2.5 mt-3 lg:mt-auto font-medium text-sm transition-all duration-200 rounded-lg ${
                        isDisabled
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : selectedPlan === planKey
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : 'bg-white border border-blue-600 text-blue-600 hover:bg-blue-50'
                      }`}
                    >
                      {isDisabled
                        ? translateInterfaceText("Plano Atual")
                        : selectedPlan === planKey
                        ? translateInterfaceText("Plano Selecionado")
                        : translateInterfaceText("Selecionar plano")}
                    </button>
                  </div>
                </div>
                );
              })}
            </div>
          </div>

          {/* Right Column - Payment Form */}
          <div className="flex flex-col">
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200 flex-1 flex flex-col h-full">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-gray-900 mb-1">{translateInterfaceText("Preencha seus dados")}</h2>
              </div>

              {/* Mobile: Selected Plan Summary */}
              <div className="lg:hidden mb-4 p-2.5 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm">
                      {translateInterfaceText("Plano")}{" "}{currentPlan.name}
                    </h3>
                    {hasFirstMonthDiscount && (
                      <p className="text-xs font-medium text-blue-700">
                        {discountBadge}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    {hasFirstMonthDiscount ? (
                      <>
                        <p className="font-bold text-gray-900">R$ {formatBRL(firstMonthPrice)}</p>
                        <p className="text-xs text-gray-600">
                          <span className="line-through">R$ {currentPlan.price}</span> {" "}{translateInterfaceText("no 1º mês")}</p>
                      </>
                    ) : (
                      <>
                        <p className="font-bold text-gray-900">R$ {currentPlan.price}</p>
                        <p className="text-xs text-gray-600">{translateInterfaceText("/mês")}</p>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <form noValidate={!!pendingCheckout} onSubmit={handleSubmit} className="space-y-3 flex-1 flex flex-col">
                {/* Cartões Salvos */}
                {savedCards.length > 0 && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      {translateInterfaceText("Forma de pagamento")}</label>
                    <RadioGroup value={useNewCard ? 'new' : selectedCardId} onValueChange={(value) => {
                      if (value === 'new') {
                        setUseNewCard(true);
                      } else {
                        setUseNewCard(false);
                        setSelectedCardId(value);
                      }
                    }}>
                      <div className="space-y-2">
                        {savedCards.map((card: SavedCard) => {
                          const isSelected = !useNewCard && selectedCardId === card.id_Stripe_Card;
                          return (
                            <div
                              key={card.id_Stripe_Card}
                              className={`flex items-center space-x-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                                isSelected
                                  ? 'border-blue-600 bg-blue-50'
                                  : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                              }`}
                            >
                              <RadioGroupItem value={card.id_Stripe_Card} id={card.id_Stripe_Card} />
                              <Label htmlFor={card.id_Stripe_Card} className="flex-1 cursor-pointer flex items-center gap-3">
                                <CardBrandIcon brand={card.brand || 'unknown'} className="w-12 h-8 flex-shrink-0" />
                                <div className="flex flex-col">
                                  <span className="text-sm font-semibold text-gray-900">
                                    •••• {card.last_Digits}
                                  </span>
                                  <span className="text-xs text-gray-500 capitalize">
                                    {card.brand || translateInterfaceText("Cartão")}
                                  </span>
                                </div>
                                {card.default_Card && (
                                  <span className="ml-auto text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                                    {translateInterfaceText("Padrão")}</span>
                                )}
                              </Label>
                            </div>
                          );
                        })}
                        <div
                          className={`flex items-center space-x-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                            useNewCard
                              ? 'border-blue-600 bg-blue-50'
                              : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                          }`}
                        >
                          <RadioGroupItem value="new" id="new-card" />
                          <Label htmlFor="new-card" className="flex-1 cursor-pointer flex items-center gap-2">
                            <Plus className="h-4 w-4 text-gray-600" />
                            <span className="text-sm font-medium">{translateInterfaceText("Adicionar novo cartão")}</span>
                          </Label>
                        </div>
                      </div>
                    </RadioGroup>
                  </div>
                )}

                {/* Campos de novo cartão - só aparecem se useNewCard estiver true */}
                {useNewCard && (
                  <>
                    {/* Card Number */}
                    <div className="space-y-2">
                      <label htmlFor="cardNumber" className="text-sm font-medium text-gray-700">
                        {translateInterfaceText("Número do cartão")}</label>
                      <div className="relative">
                        <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                        <div className="pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-500">
                          <CardNumberElement
                            options={{
                              style: {
                                base: {
                                  fontSize: '14px',
                                  color: '#374151',
                                  lineHeight: '20px',
                                  '::placeholder': {
                                    color: '#9ca3af',
                                  },
                                },
                              },
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Card Name */}
                    <div className="space-y-2">
                      <label htmlFor="cardName" className="text-sm font-medium text-gray-700">
                        {translateInterfaceText("Nome impresso no cartão")}</label>
                      <input
                        id="cardName"
                        type="text"
                        placeholder={translateInterfaceText("Nome Completo")}
                        className="w-full px-3 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                        value={cardName}
                        onChange={(e) => setCardName(e.target.value)}
                        required
                      />
                    </div>

                    {/* Expiry and CVV */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <label htmlFor="expiryDate" className="text-sm font-medium text-gray-700">
                          {translateInterfaceText("Validade")}</label>
                        <div className="px-3 py-3 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-500">
                          <CardExpiryElement
                            options={{
                              style: {
                                base: {
                                  fontSize: '14px',
                                  color: '#374151',
                                  lineHeight: '20px',
                                  '::placeholder': {
                                    color: '#9ca3af',
                                  },
                                },
                              },
                            }}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label htmlFor="cvv" className="text-sm font-medium text-gray-700">
                          {translateInterfaceText("CVC")}</label>
                        <div className="px-3 py-3 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-500">
                          <CardCvcElement
                            options={{
                              style: {
                                base: {
                                  fontSize: '14px',
                                  color: '#374151',
                                  lineHeight: '20px',
                                  '::placeholder': {
                                    color: '#9ca3af',
                                  },
                                },
                              },
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Coupon */}
                <div className="space-y-2">
                  <label htmlFor="couponCode" className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    {isCouponLocked ? translateInterfaceText("Cupom de desconto aplicado") : translateInterfaceText("Cupom de desconto (opcional)")}
                    {hasFirstMonthDiscount && (
                      <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">
                        {discountBadge}
                      </span>
                    )}
                  </label>
                  <div className="relative">
                    <input
                      id="couponCode"
                      type="text"
                      placeholder={translateInterfaceText("Digite seu cupom")}
                      readOnly={isCouponLocked}
                      aria-readonly={isCouponLocked}
                      className={`w-full px-3 py-2.5 text-sm border rounded-lg focus:ring-2 ${
                        isCouponLocked
                          ? 'border-blue-300 bg-blue-50 font-semibold text-blue-800 pr-10 cursor-not-allowed select-none focus:ring-blue-500/20 focus:border-blue-500'
                          : couponStatus === 'invalid'
                          ? 'border-red-400 bg-red-50 pr-10 focus:ring-red-500/20 focus:border-red-500'
                          : couponStatus === 'valid'
                          ? 'border-blue-400 bg-blue-50 pr-10 focus:ring-blue-500/20 focus:border-blue-500'
                          : 'border-gray-300 pr-10 focus:ring-blue-500/20 focus:border-blue-500'
                      }`}
                      value={couponCode}
                      onChange={(e) => {
                        if (isCouponLocked) return;
                        setCouponCode(e.target.value);
                        // Limpa o status ao editar; revalida só no próximo blur.
                        if (couponStatus !== 'idle') {
                          setCouponStatus('idle');
                          setValidatedCoupon(null);
                        }
                      }}
                      onBlur={handleCouponBlur}
                    />
                    {/* Ícone de status à direita do input */}
                    {isCouponLocked ? (
                      <Lock className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-blue-600 pointer-events-none" />
                    ) : couponStatus === 'validating' ? (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin pointer-events-none" />
                    ) : couponStatus === 'valid' ? (
                      <Check className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-blue-600 pointer-events-none" />
                    ) : couponStatus === 'invalid' ? (
                      <X className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-red-500 pointer-events-none" />
                    ) : null}
                  </div>
                  {isCouponLocked && (
                    <p className="text-xs text-gray-500">
                      {translateInterfaceText("Cupom promocional aplicado automaticamente e não pode ser removido.")}</p>
                  )}
                  {!isCouponLocked && couponStatus === 'validating' && (
                    <p className="text-xs text-gray-500">{translateInterfaceText("Validando cupom...")}</p>
                  )}
                  {!isCouponLocked && couponStatus === 'invalid' && (
                    <p className="text-xs font-medium text-red-600">
                      {translateInterfaceText("Não encontramos um cupom com esse nome.")}</p>
                  )}
                  {!isCouponLocked && couponStatus === 'valid' && (
                    <p className="text-xs font-medium text-blue-600">
                      {translateInterfaceText("Cupom aplicado")}{discountBadge ? ` — ${discountBadge}` : ''}.
                    </p>
                  )}
                </div>

                {/* Resumo do valor (já com o cálculo do cupom) */}
                <div className="mt-1 rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-1.5">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">{translateInterfaceText("Plano")}{" "}{currentPlan.name}</span>
                    <span className={hasFirstMonthDiscount ? 'text-gray-400 line-through' : 'font-medium text-gray-900'}>
                      R$ {currentPlan.price}{translateInterfaceText("/mês")}</span>
                  </div>
                  {hasFirstMonthDiscount && (
                    <>
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium text-blue-700">{discountSummaryLeft}</span>
                        <span className="font-bold text-blue-700">R$ {formatBRL(firstMonthPrice)}</span>
                      </div>
                      {showAfterPrice && (
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>{translateInterfaceText("Depois")}</span>
                          <span>R$ {currentPlan.price}{translateInterfaceText("/mês")}</span>
                        </div>
                      )}
                    </>
                  )}
                  {eligibleForTrial && (
                    <div className="flex items-center gap-1.5 pt-1 text-xs text-blue-600">
                      <Check className="h-3.5 w-3.5 flex-shrink-0" />
                      {translateInterfaceText("15 dias grátis — você só paga após o período de teste")}</div>
                  )}
                </div>

                {/* Terms Text */}
                <div className="py-1 mt-4">
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {translateInterfaceText("Ao clicar nesse botão você aceita e concorda com nossos termos de uso")}</p>
                </div>

                {/* Submit Button */}
                <div className="mt-auto">
                  <button
                    type="submit"
                    disabled={isLoading || checkingPending || !!pendingError || (!pendingCheckout && (!stripe || fetchingPlan || planError || (!isTrial && activeSubscription?.status === 'active' && idStripeProduct === selectedProduct)))}
                    className={`w-full py-3 font-semibold text-sm transition-all duration-200 rounded-lg shadow-lg hover:shadow-xl flex items-center justify-center ${
                      isLoading || checkingPending || !!pendingError || (!pendingCheckout && (!stripe || planError || fetchingPlan))
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    <Lock className="mr-2 h-4 w-4" />
                    {isLoading ? translateInterfaceText("Processando...") : checkingPending ? translateInterfaceText("Consultando pagamentos...") : pendingCheckout ? translateInterfaceText("Consultar pagamento pendente") : translateInterfaceText("Ativar agora")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
